import React from 'react'
import { useHandleCopyValue } from '@sketch/modules-common'
import { Tooltip } from '@sketch/components'
import {
  PositionContainer,
  BorderContainer,
  PaddingContainer,
  DimensionContainer,
  BoxText,
  BorderText,
  PositionText,
  NoPaddingText,
  TopSizeText,
  BottomSizeText,
  LeftSizeText,
  RightSizeText,
  TopLeftText,
  TopRightText,
  BottomRightText,
  BottomLeftText,
  BoxSizingText,
  TopMarginArrow,
  BottomMarginArrow,
  LeftMarginArrow,
  RightMarginArrow,
  TopMargin,
  BottomMargin,
  LeftMargin,
  RightMargin,
  ClickableArea,
} from './BoxModel.styles'
import {
  SketchNonGraphicLayerElement,
  SketchTextLayerElement,
  SketchShapeLayerElement,
  SketchSymbolInstanceLayerElement,
  SketchImageLayerElement,
  SketchBasicFrameElement,
  SketchElement,
  SketchFrameElement,
} from '../../../../inspector'
import { StackPadding } from '../../../../inspector/types/InspectorTypes/Stack'
import { isMaxCornerRadius } from '../../../utils/corners'

type BoxModelProps = {
  element: SketchLayerElement | SketchFrameElement
}

interface PositionBoxProps {
  measurementData: {
    leftMargin?: number
    topMargin?: number
    rightMargin?: number
    bottomMargin?: number
  }
}

interface BorderBoxProps {
  enabledBorders: boolean
  borderRadius?: number[]
  borderSize?: number
}

interface PaddingBoxProps {
  padding?: StackPadding
}

type SketchLayerElement =
  | SketchNonGraphicLayerElement
  | SketchTextLayerElement
  | SketchShapeLayerElement
  | SketchSymbolInstanceLayerElement
  | SketchImageLayerElement

type ElementWithCorners = SketchLayerElement & {
  corners?: {
    radius: number[]
  }
}

const roundFormula = new Intl.NumberFormat(['en'], {
  style: 'decimal',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const round = (value: number) => {
  if (isNaN(value)) return 0
  return roundFormula.format(value)
}

const hasMoreThanFourDigits = (value: number) => {
  const str = Math.abs(value).toString().replace('.', '')
  return str.length > 4
}

// An empty array means no rounded corners.
// If the array isn't as long as the number of corners, repeat the last element
const normalizeCorners = (input: number[] | undefined) => {
  if (!Array.isArray(input) || input.length === 0) {
    return undefined
  }

  const result = [...input]
  const lastValue = result[result.length - 1]

  while (result.length < 4) {
    result.push(lastValue)
  }

  return result.slice(0, 4) // in case input has more than 4 elements
}

type SketchLayerOrigin = {
  x: number
  y: number
}

const originFromCanvasFrame = (element: SketchElement): SketchLayerOrigin => {
  const offset: SketchLayerOrigin = { x: 0, y: 0 }

  let node = element
  while (node.frameAncestorElement !== undefined) {
    offset.x += node.frameRelativeBoxRect.x
    offset.y += node.frameRelativeBoxRect.y
    node = node.frameAncestorElement
  }

  return offset
}

/**
 * Go through the chain of frame ancestor elements to find the most top frame
 * that contains the element.
 * If no frame ancestor is found, return the element itself.
 */
const getMostTopFrame = (element: SketchElement): SketchElement => {
  if (element.frameAncestorElement !== undefined) {
    return getMostTopFrame(element.frameAncestorElement)
  }

  return element
}

const CopiableText = ({
  value,
  showNumber,
}: {
  value: number | string
  showNumber?: boolean
}) => {
  const roundedTextValue =
    typeof value === 'number' ? round(value).toString() : value

  const {
    handleCopyValue,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipText,
    copyTooltipVisible,
  } = useHandleCopyValue(roundedTextValue)

  return (
    <Tooltip
      placement="left"
      spacing="10px"
      visible={copyTooltipVisible}
      content={
        showNumber ? `${copyTooltipText} ${roundedTextValue}` : copyTooltipText
      }
    >
      {tooltipProps => (
        <ClickableArea
          {...tooltipProps}
          onClick={handleCopyValue}
          onMouseEnter={handleEnterCopiableArea}
          onMouseLeave={handleLeaveCopiableArea}
        >
          {roundedTextValue}
        </ClickableArea>
      )}
    </Tooltip>
  )
}

const PositionBox: React.FCC<PositionBoxProps> = ({
  children,
  measurementData,
}) => {
  const { leftMargin, topMargin, rightMargin, bottomMargin } =
    measurementData || {}

  const isBigLeft = hasMoreThanFourDigits(leftMargin || 0)
  const isBigRight = hasMoreThanFourDigits(rightMargin || 0)

  return (
    <PositionContainer>
      <PositionText>position</PositionText>
      <TopMarginArrow />
      <LeftMarginArrow />
      <RightMarginArrow />
      <BottomMarginArrow />
      {topMargin ? (
        <TopMargin>
          <CopiableText value={topMargin} />
        </TopMargin>
      ) : null}
      {bottomMargin ? (
        <BottomMargin>
          <CopiableText value={bottomMargin} />
        </BottomMargin>
      ) : null}
      {leftMargin ? (
        <LeftMargin isBig={isBigLeft}>
          <CopiableText showNumber={isBigLeft} value={leftMargin} />
        </LeftMargin>
      ) : null}
      {rightMargin ? (
        <RightMargin isBig={isBigRight}>
          <CopiableText showNumber={isBigRight} value={rightMargin} />
        </RightMargin>
      ) : null}

      {children}
    </PositionContainer>
  )
}

const BorderBox: React.FCC<BorderBoxProps> = ({
  children,
  enabledBorders,
  borderRadius,
  borderSize,
}) => {
  const formatBorderRadiusValue = (value: number) =>
    isMaxCornerRadius(value) ? 'Max' : value

  return (
    <BorderContainer>
      {enabledBorders || borderRadius ? (
        <>
          <BorderText>border</BorderText>

          {enabledBorders && borderSize && (
            <>
              <TopSizeText>
                <CopiableText value={borderSize} />
              </TopSizeText>
              <BottomSizeText>
                <CopiableText value={borderSize} />
              </BottomSizeText>
              <LeftSizeText>
                <CopiableText value={borderSize} />
              </LeftSizeText>
              <RightSizeText>
                <CopiableText value={borderSize} />
              </RightSizeText>
            </>
          )}

          {borderRadius && (
            <>
              <TopLeftText>
                <CopiableText
                  value={formatBorderRadiusValue(borderRadius[0])}
                />
              </TopLeftText>
              <TopRightText>
                <CopiableText
                  value={formatBorderRadiusValue(borderRadius[1])}
                />
              </TopRightText>
              <BottomRightText>
                <CopiableText
                  value={formatBorderRadiusValue(borderRadius[2])}
                />
              </BottomRightText>
              <BottomLeftText>
                <CopiableText
                  value={formatBorderRadiusValue(borderRadius[3])}
                />
              </BottomLeftText>
            </>
          )}
        </>
      ) : (
        <BorderText>no border</BorderText>
      )}

      {children}
    </BorderContainer>
  )
}

const PaddingBox: React.FCC<PaddingBoxProps> = ({ children, padding }) => {
  return (
    <PaddingContainer hasPadding={!!padding}>
      {padding ? (
        <>
          <BoxText>padding</BoxText>
          <TopSizeText>
            <CopiableText value={padding.top} />
          </TopSizeText>
          <BottomSizeText>
            <CopiableText value={padding.bottom} />
          </BottomSizeText>
          <LeftSizeText>
            <CopiableText value={padding.left} />
          </LeftSizeText>
          <RightSizeText>
            <CopiableText value={padding.right} />
          </RightSizeText>
        </>
      ) : (
        <NoPaddingText>no padding</NoPaddingText>
      )}

      {children}
    </PaddingContainer>
  )
}

export const BoxModel = ({ element }: BoxModelProps) => {
  const elementWithCorners = element as ElementWithCorners

  const width = element.layerSize.width
  const height = element.layerSize.height
  const stack =
    'stack' in element
      ? (element.stack as SketchBasicFrameElement['stack'])
      : undefined
  const padding = stack?.padding

  const borders =
    element.style && 'borders' in element.style
      ? element.style?.borders
      : undefined

  const enabledBorders = borders ? borders[0]?.isEnabled : false

  const border = borders ? borders[0] : undefined
  const borderSize = border?.thickness

  const borderRadius = elementWithCorners.corners?.radius

  const borderRadiusWithValue = borderRadius?.some(n => n > 0)
    ? borderRadius
    : undefined

  const normalizedBorderRadius = normalizeCorners(borderRadiusWithValue)

  // This grabs the canvas level frame that the element is a child of
  // Not the closest parent frame.
  // TODO: we probably could merge getMostTopFrame with originFromCanvasFrame
  // to calculate everything in one go.
  const parentArtboardDimensions = getMostTopFrame(element).layerSize
  const elementOriginFromCanvasFrame = originFromCanvasFrame(element)

  const measurements = {
    leftMargin: elementOriginFromCanvasFrame.x,
    topMargin: elementOriginFromCanvasFrame.y,
    rightMargin:
      parentArtboardDimensions.width -
      (elementOriginFromCanvasFrame.x + element.frameRelativeBoxRect.width),
    bottomMargin:
      parentArtboardDimensions.height -
      (elementOriginFromCanvasFrame.y + element.frameRelativeBoxRect.height),
  }

  const widthWithoutPaddings = round(
    width - (padding?.left || 0) - (padding?.right || 0)
  )
  const heightWithoutPaddings = round(
    height - (padding?.top || 0) - (padding?.bottom || 0)
  )

  return (
    <>
      <PositionBox measurementData={measurements}>
        <BorderBox
          borderRadius={normalizedBorderRadius}
          enabledBorders={enabledBorders}
          borderSize={borderSize}
        >
          <PaddingBox padding={padding}>
            <DimensionContainer>
              {widthWithoutPaddings}x{heightWithoutPaddings}
            </DimensionContainer>
          </PaddingBox>
        </BorderBox>
      </PositionBox>
      <BoxSizingText>border-box</BoxSizingText>
    </>
  )
}
